/**
 * `useLocalStorage` — type-safe two-way sync between a `Ref<T>` and
 * `window.localStorage`.
 *
 *   const theme = useLocalStorage<'light' | 'dark'>('wwm.theme', 'light')
 *   theme.value = 'dark'   // persisted as JSON under "wwm.theme"
 *
 * Reads:
 *   - On first call, the key is parsed from `localStorage`. If the key is
 *     missing, malformed JSON, or fails the optional runtime validation, the
 *     default value is returned.
 *
 * Writes:
 *   - Mutations to the returned ref update `localStorage` synchronously.
 *   - Storage quota / disabled-storage errors are forwarded to `onError`; if
 *     `onError` is not supplied the original error is rethrown so the caller
 *     cannot swallow quota problems silently.
 *   - Storage events from other tabs are mirrored back into the ref.
 *
 * Validation:
 *   - The default JSON parser returns `unknown` and narrows via the optional
 *     `validate` type guard (e.g. `(raw): raw is BoardState => isBoardState(raw)`).
 *     When `validate` rejects a value, the default value is restored. Without
 *     a guard, any value that round-trips through `JSON.parse` is accepted.
 *
 * The serializer is JSON by default. Pass `serializer` for non-JSON formats
 * (e.g. `URLSearchParams`, BigInt-safe encodings, etc.).
 *
 * Scope constraint:
  *   - `useLocalStorage` MUST be called inside an active Vue/Pinia effect
  *     scope (e.g. a `setup()` function, a `computed()`, a Pinia store
  *     getter/action). The cross-tab `storage` event listener is registered on
  *     `window` and only detached via `onScopeDispose`; calling this from a
  *     plain module top-level, a one-off test, or any non-scope context will
  *     leak the listener for the lifetime of the page.
  */
import { getCurrentScope, onScopeDispose, ref, watch } from 'vue'
import type { Ref } from 'vue'

export interface Serializer<T> {
  readonly read: (raw: string) => T
  readonly write: (value: T) => string
}

export interface UseLocalStorageOptions<T> {
  /**
   * Custom codec for non-JSON formats. When omitted, the built-in JSON codec
   * is used; its `read` returns `unknown` and is paired with `validate` to
   * narrow to `T`.
   */
  readonly serializer?: Serializer<T>
  /**
   * Runtime type guard applied to values decoded by the default JSON codec.
   * Values that fail the guard fall back to `defaultValue`. Required when
   * using the default codec with anything stricter than `unknown`.
   */
  readonly validate?: (raw: unknown) => raw is T
  /**
   * Called when `setItem` throws (quota exceeded, storage disabled, …). If
   * omitted, the original error is rethrown so quota problems can't be
   * swallowed by accident.
   */
  readonly onError?: (err: unknown) => void
}

function defaultSerializer(): Serializer<unknown> {
  return {
    read: (raw) => JSON.parse(raw) as unknown,
    write: (value) => JSON.stringify(value),
  }
}

export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  options: UseLocalStorageOptions<T> = {},
): Ref<T> {
  const { serializer = defaultSerializer() as Serializer<T>, validate, onError } = options

  function decode(raw: string): T {
    const parsed: unknown = serializer.read(raw)
    if (validate && !validate(parsed)) return defaultValue
    return parsed as T
  }

  const stored = ref<T>(readInitial(key, defaultValue, decode))

  // Persist changes from this tab.
  watch(
    stored,
    (next) => {
      try {
        window.localStorage.setItem(key, serializer.write(next))
      } catch (err: unknown) {
        if (onError) onError(err)
        else throw err
      }
    },
    { deep: true },
  )

  // Mirror updates from other tabs.
  if (typeof window !== 'undefined') {
    const handler = (event: StorageEvent): void => {
      if (event.key !== key || event.newValue === null) return
      try {
        stored.value = decode(event.newValue)
      } catch {
        stored.value = defaultValue
      }
    }
    window.addEventListener('storage', handler)
    // Detach on scope disposal so we don't leak the listener across HMR or
    // store teardowns. The `getCurrentScope()` guard is defensive: per the
    // scope-constraint JSDoc above, callers must invoke `useLocalStorage`
    // inside an active effect scope, so this branch should always run in
    // practice. Falling through to the no-dispose path is a leak.
    if (getCurrentScope()) {
      onScopeDispose(() => window.removeEventListener('storage', handler))
    }
  }

  // `ref<T>(value)` resolves to `Ref<UnwrapRef<T>, UnwrapRef<T> | T>`, which
  // isn't structurally assignable to the declared `Ref<T>` return. The cast
  // is contained here so callers see the stable `Ref<T>` contract. The store
  // and every component replace `.value` wholesale (never mutate fields in
  // place) — `ref` is intentional; switching to `shallowRef` would skip deep
  // tracking that other consumers might rely on.
  return stored as Ref<T>
}

function readInitial<T>(key: string, defaultValue: T, decode: (raw: string) => T): T {
  if (typeof window === 'undefined') return defaultValue
  try {
    const raw = window.localStorage.getItem(key)
    if (raw === null) return defaultValue
    return decode(raw)
  } catch {
    return defaultValue
  }
}
